import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import ReactDOM from "react-dom";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal } from "lucide-react";
import PaginationControls from "../../../../components/ACS-Dashboard/ACS-Blocks/PaginationControls";
import { usePagination } from "../hooks/usePagination";
import { useDataFetching } from "../hooks/useDataFetching";
import { useDelete } from "../hooks/useDeleteHandler";
import { contractApi } from "../api/contractApi";
import Dialog from "../components/Dialog";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { useDispatch } from "react-redux";
import { addTab } from "../../../../redux/slices/contractsSlice";
import { formatAsLocalDate } from "../utils/formatters";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";

const HEADERS = [
  "Selection",
  "Action",
  "Contract Code",
  "Status",
  "Payment Method",
  "Office Code",
  "Effective Date",
  "Expiration Date",
  "Assoc.Lanes",
];

const SORTABLE_COLUMNS = {
  "Contract Code":  "contractCode",
  "Status":         "status",
  "Payment Method": "paymentMethod",
  "Office Code":    "officeCode",
  "Effective Date": "effectiveDate",
  "Expiration Date":"expirationDate",
  "Assoc.Lanes":    "assocLanes",   // in-memory sort
};

const statusBadgeClass = {
  DRAFT: "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200",
  ACTIVE: "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200",
  EXPIRED: "bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200",
};

const ContractsDetails = ({
  officeCode,
  onContractsFormatted,
  searchTerm = null,
  fetchFn = null,
}) => {
  const { pagination, setPagination, handlePageChange, handlePerPageChange } =
    usePagination();
    const [sortBy, setSortBy] = useState("updatedAt");
    const [ascending, setAscending] = useState(false);

  const [contracts,          setContracts]         = useState([]);
  const [selectedContracts,  setSelectedContracts]  = useState([]);
  const [isLoading,          setIsLoading]          = useState(true);
  const [openMenu,           setOpenMenu]           = useState(null);
  const [dropdownPos,        setDropdownPos]        = useState({ top: 0, left: 0 });
  const [columnWidths, setColumnWidths] = useState({});

  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const dispatch          = useDispatch();
  const tableViews = useTableSavedViews({
    pageKey: TABLE_PAGE_KEYS.CONTRACTS,
    allHeaders: HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Contract Code"],
  });
  const {
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  const {
    handleDelete,
    error: deleteError,
    resetError,
    DeleteConfirmationDialog,
  } = useDelete({
    deleteApi:       (contractId) => contractApi.deleteContract(officeCode, contractId),
    DialogComponent: Dialog,
    onSuccess:       () => refetch(),
    onError:         (error) => { console.error("Delete error:", error); resetError(); },
  });

    const handleSort = (newSortBy) => {
        if (sortBy !== newSortBy) {
            setSortBy(newSortBy);
            setAscending(true);
        } else {
            setAscending((prev) => !prev);
        }
    };

    const onHeaderSortClick = (headerLabel) => {
        const key = SORTABLE_COLUMNS[headerLabel];
        if (!key) return;
        handleSort(key);
    };

  const fetchContracts = useCallback(
    (params) => {
      if (searchTerm && fetchFn) {
        return fetchFn({
          officeCode,
          query:  searchTerm,
          status: "",
          page: pagination.currentPage - 1,
          size: pagination.perPage,
          sortBy,
          ascending,
          ...params,
        });
      }

      // Otherwise use the regular searchContracts API
      return contractApi.searchContracts({
        officeCode,
        page: pagination.currentPage - 1,
        size: pagination.perPage,
        sortBy,
        ascending,
        ...params,
      });
    },
    [searchTerm, fetchFn, officeCode, pagination.currentPage, pagination.perPage, sortBy, ascending]
  );

  const { refetch } = useDataFetching({
    fetchFn:   fetchContracts,
    officeCode,
    onSuccess: useCallback(
      (data) => {
        setContracts(data.content || []);
        setPagination((prev) => ({ ...prev, total: data.totalElements || 0 }));
        setIsLoading(false);
        onContractsFormatted?.(data.totalElements || 0);
      },
      [setPagination, onContractsFormatted]
    ),
    onError: (error) => { console.error("Fetch error:", error); setIsLoading(false); },
  });

  useEffect(() => {
    setIsLoading(true);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Close portal dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenMenu(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenMenu((prev) => (prev === id ? null : id));
  };

  const handleSelectAll = (checked) =>
    setSelectedContracts(checked ? contracts.map((c) => c.id) : []);

  const handleSingleCheckbox = (contractId) =>
    setSelectedContracts((prev) =>
      prev.includes(contractId)
        ? prev.filter((id) => id !== contractId)
        : [...prev, contractId]
    );

  const openContractTab = (contract) => {
    dispatch(addTab({
      tabData: {
        name:       `Contract: ${contract["Contract Code"]}`,
        type:       "contractDetails",
        contractId: contract.originalId || contract.id,
        contractData: contract,
      },
    }));
  };

  const formattedContracts = useMemo(
    () =>
      contracts.map((contract) => ({
        ...contract,
        id:               contract.id,
        "Contract Code":  contract.contractCode,
        Status: (
          <span className={`px-2 py-1 rounded font-medium ${statusBadgeClass[contract.status] || ""}`}>
            {contract.status}
          </span>
        ),
        "Payment Method":  contract.paymentMethod || "-",
        "Office Code":     contract.officeCode    || "-",
        "Effective Date":  contract.effectiveDate  ? formatAsLocalDate(contract.effectiveDate)  : "-",
        "Expiration Date": contract.expirationDate ? formatAsLocalDate(contract.expirationDate) : "-",
        "Assoc.Lanes":     contract.laneIds?.length || contract.assocLanes || 0,
      })),
    [contracts]
  );

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader
          color="text-blue-600 dark:text-acs-dark-text"
          size="h-5 w-5"
          message={searchTerm ? "Searching Contracts..." : "Loading Contracts..."}
        />
      ) : (
        <>
          {searchTerm && (
            <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
              Showing search results for: <span className="font-semibold">"{searchTerm}"</span>
              {contracts.length === 0 && " — No contracts found"}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
            <TableSavedViewsControl
              controlLabel={controlLabel}
              views={views}
              activeView={activeView}
              loading={viewsLoading}
              error={viewsError}
              isDirty={isDirty}
              allDataHeaders={HEADERS.slice(2)}
              dataHeaders={dataHeaders}
              onSelectView={selectView}
              onSaveNew={saveNewView}
              onUpdateActive={updateActiveView}
              onRename={renameView}
              onDelete={deleteView}
              onSetDefault={setDefaultView}
              onUpdateColumnVisibility={updateColumnVisibility}
              onManageColumns={onManageColumns}
            />
          </div>

          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              {...columnManagerProps}
              data={formattedContracts}
              initialColumnWidths={columnWidths}
              onColumnResize={setColumnWidths}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              selectedRows={selectedContracts}
              totalValidRows={formattedContracts.length}
              onSelectAll={handleSelectAll}
              renderCheckboxActions={() => {}}
              checkboxHeaderRef={checkboxHeaderRef}
              sortBy={sortBy}
              ascending={ascending}
              onSort={onHeaderSortClick}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
            >
              {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
                const id     = row.id;
                const isOpen = openMenu === id;
                const orderedDataHeaders = (visibleColumns ?? [])
                  .map((col) => col.id)
                  .filter((id) => id !== "Selection" && id !== "Action");
                const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

                const actions = [
                  {
                    icon:   "Eye",
                    label:  "View",
                    color:  "blue",
                    action: () => openContractTab(row),
                  },
                  {
                    icon:   "Edit",
                    label:  "Edit",
                    color:  "green",
                    action: () => {/* setSelectedContract(row) */},
                  },
                  {
                    icon:   "Trash",
                    label:  "Delete",
                    color:  "red",
                    action: () =>
                      handleDelete(id).catch((err) => {
                        if (err.message !== "User cancelled") console.error(err);
                      }),
                  },
                ];

                return (
                  <>
                    {/* Column 0 — Checkbox */}
                    <td
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${getStickyCellProps("Selection").className}`}
                      style={{ ...getStickyCellProps("Selection").style, width: 80, minWidth: 80, maxWidth: 80 }}
                    >
                      <input
                        type="checkbox"
                        className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                        checked={selectedContracts.includes(id)}
                        onChange={() => handleSingleCheckbox(id)}
                      />
                    </td>

                    {/* Column 1 — Action (portal dropdown) */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${getStickyCellProps("Action").className}`}
                      style={{ ...getStickyCellProps("Action").style, width: 50, minWidth: 50, maxWidth: 50 }}
                    >
                      <button
                        data-action-trigger
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                        onClick={(e) => handleActionClick(id, e)}
                      >
                        <MoreHorizontal size={18} className="mx-auto" />
                      </button>
                    </td>

                    {isOpen && ReactDOM.createPortal(
                      <div
                        ref={dropdownRef}
                        style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                      >
                        <ActionMenu
                          rowId={id}
                          isOpen
                          moduleName="Contract"
                          onClose={() => setOpenMenu(null)}
                          actions={actions}
                        />
                      </div>,
                      document.body
                    )}

                    {/* Columns 2+ — Data */}
                    {dataHeadersToRender.map((col) => {
                      return (
                        <td
                          key={col}
                          className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${getStickyCellProps(col).className}`}
                          style={{
                            ...getStickyCellProps(col).style,
                            whiteSpace:   "nowrap",
                            overflow:     "hidden",
                            textOverflow: "ellipsis",
                            width:        "200px",
                            minWidth:     "200px",
                            maxWidth:     "200px",
                          }}
                          onDoubleClick={() => openContractTab(row)}
                        >
                          {row[col]}
                        </td>
                      );
                    })}
                  </>
                );
              }}
            </Table>
          </div>

          <PaginationControls
            pagination={pagination}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            moduleName="Contracts"
            className="mt-4 p-2 border-t border-gray-200 dark:border-acs-dark-border"
          />

          <DeleteConfirmationDialog className="z-[1001]" />

          {deleteError && (
            <div className="fixed bottom-4 right-4 bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded">
              <span className="block sm:inline">{deleteError.message}</span>
              <button onClick={resetError} className="absolute top-0 right-0 px-2 py-1" />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContractsDetails;
